import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { CLINIC_NAME, DOCTOR_NAME, PHONE_PRIMARY } from "@/lib/constants";
import { Copy, ExternalLink, RotateCcw, Sparkles, Star } from "lucide-react";

// Update this once you have the clinic's Google Place ID for a 1-click review link.
const GOOGLE_REVIEW_URL =
  "https://www.google.com/search?q=Mutha+Advanced+Dental+Care+Kalyan+West+reviews";

const TREATMENTS = [
  "Braces / Aligners",
  "Crowns & Bridges",
  "Dental Implants",
  "Dentures",
  "Full Mouth Rehab",
  "Gum Treatment",
  "Kids Dentistry",
  "Microscopic RCT",
  "Root Canal",
  "Scaling & Polishing",
  "Smile Makeover",
  "Teeth Whitening",
  "Tooth Extraction",
  "Veneers",
  "Wisdom Tooth Removal",
] as const;

const DOCTORS = [DOCTOR_NAME, "Dr. Vivek Mutha", "Associate Doctor"] as const;

type PerkGroup = { title: string; emoji: string; items: string[] };

const CLINICAL_BY_TREATMENT: Record<string, string[]> = {
  "Braces / Aligners": [
    "Advanced digital OPG / iTero 3D scanner",
    "Digital X-rays with instant on-screen view",
    "Microscope-assisted precision treatment for precise bracket placement",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Predictive 3D treatment outcome simulation",
  ],
  "Crowns & Bridges": [
    "Advanced digital OPG / iTero 3D scanner",
    "Digital X-rays with instant on-screen view",
    "Microscope-assisted precision treatment for perfect margin checking",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Precision digital bite mapping",
  ],
  "Dental Implants": [
    "Advanced digital OPG / iTero 3D scanner",
    "Digital X-rays with instant on-screen view",
    "Microscope-assisted precision treatment",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Computer-guided implant surgery planning",
  ],
  "Dentures": [
    "Advanced digital OPG / iTero 3D scanner",
    "Digital X-rays with instant on-screen view",
    "Microscope-assisted precision treatment",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "High-accuracy digital jaw relation tracking",
  ],
  "Full Mouth Rehab": [
    "Advanced digital OPG / iTero 3D scanner",
    "Microscope-assisted precision treatment",
    "Digital X-rays with instant on-screen view",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Comprehensive 3D facial & jaw alignment analysis",
  ],
  "Gum Treatment": [
    "Microscope-assisted precision treatment for deep root planing",
    "Digital X-rays with instant on-screen view",
    "Advanced digital OPG / iTero 3D scanner",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Advanced minimally invasive dental lasers",
  ],
  "Kids Dentistry": [
    "Digital X-rays with instant on-screen view",
    "Single-use disposable hygienic kits",
    "100% sterilized & autoclaved instruments",
    "Advanced digital OPG / iTero 3D scanner",
    "Microscope-assisted precision treatment",
    "Ultra-conservative, drill-free cavity preparation",
  ],
  "Microscopic RCT": [
    "Microscope-assisted precision treatment",
    "Digital X-rays with instant on-screen view",
    "Advanced digital OPG / iTero 3D scanner",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Advanced rotary files & electronic apex locators",
  ],
  "Root Canal": [
    "Digital X-rays with instant on-screen view",
    "Microscope-assisted precision treatment",
    "Advanced digital OPG / iTero 3D scanner",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Automated endodontic rotary system",
  ],
  "Scaling & Polishing": [
    "Single-use disposable hygienic kits",
    "100% sterilized & autoclaved instruments",
    "Digital X-rays with instant on-screen view",
    "Advanced digital OPG / iTero 3D scanner",
    "Microscope-assisted precision treatment",
    "Advanced ultrasonic scaling tech for stain removal",
  ],
  "Smile Makeover": [
    "Advanced digital OPG / iTero 3D scanner",
    "Microscope-assisted precision treatment",
    "Digital X-rays with instant on-screen view",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Digital Smile Design (DSD) preview software",
  ],
  "Teeth Whitening": [
    "Single-use disposable hygienic kits",
    "100% sterilized & autoclaved instruments",
    "Digital X-rays with instant on-screen view",
    "Advanced digital OPG / iTero 3D scanner",
    "Microscope-assisted precision treatment",
    "Advanced LED light-activated whitening accelerator",
  ],
  "Tooth Extraction": [
    "Digital X-rays with instant on-screen view",
    "Advanced digital OPG / iTero 3D scanner",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Microscope-assisted precision treatment",
    "Atraumatic, minimally invasive extraction techniques",
  ],
  "Veneers": [
    "Advanced digital OPG / iTero 3D scanner",
    "Microscope-assisted precision treatment for ultra-thin tooth preparation",
    "Digital X-rays with instant on-screen view",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Ultra-precise 3D digital mockups",
  ],
  "Wisdom Tooth Removal": [
    "Advanced digital OPG / iTero 3D scanner for nerve tracking",
    "Digital X-rays with instant on-screen view",
    "100% sterilized & autoclaved instruments",
    "Single-use disposable hygienic kits",
    "Microscope-assisted precision treatment",
    "Advanced piezosurgery / surgical bone cutting systems",
  ],
};

const DEFAULT_CLINICAL: string[] = [
  "Advanced digital OPG / iTero 3D scanner",
  "100% sterilized & autoclaved instruments",
  "Single-use disposable hygienic kits",
  "Microscope-assisted precision treatment",
  "Digital X-rays with instant on-screen view",
];

function shuffle<T>(arr: T[], seed: number): T[] {
  const a = [...arr];
  let s = seed || 1;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const PERK_GROUPS: PerkGroup[] = [
  {
    title: "Clinic Environment",
    emoji: "🏠",
    items: [
      "Spotless, sanitized & modern interiors",
      "Calm & stress-free atmosphere",
      "Comfortable reclining chairs",
      "Private treatment rooms",
      "Pleasant waiting area with refreshments",
    ],
  },
  {
    title: "Pricing & Operations",
    emoji: "💰",
    items: [
      "Upfront written pricing — no hidden fees",
      "Flexible EMI / installment options",
      "No unnecessary treatment pushed",
      "WhatsApp booking & quick confirmations",
      "Detailed post-care instructions on WhatsApp",
    ],
  },
  {
    title: "Doctor & Staff",
    emoji: "👨‍⚕️",
    items: [
      "Consistent care by the same doctor",
      "Extremely patient & gentle approach",
      "Thorough explanation before every step",
      "Friendly & warm front-desk staff",
      "Post-treatment follow-up call",
    ],
  },
  {
    title: "Location & Timings",
    emoji: "📍",
    items: [
      "Convenient location in Kalyan",
      "Free dedicated parking available",
      "Sunday morning slots for working professionals",
      "Extended evening appointment slots",
    ],
  },
];

const HISTORY_KEY = "madc_review_history_v1";

type HistoryItem = { id: string; name: string; review: string; createdAt: number };

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length];
}

function joinNicely(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function generateReview(opts: {
  name: string;
  treatments: string[];
  doctor: string;
  perks: string[];
}): string {
  const { name, treatments, doctor, perks } = opts;
  const seed = (name + treatments.join("") + perks.join("")).length;

  const treatmentText =
    treatments.length > 0 ? joinNicely(treatments.map((t) => t.toLowerCase())) : "dental treatment";

  const openers = [
    `I recently visited ${CLINIC_NAME} in Kalyan for ${treatmentText} and the experience was genuinely excellent.`,
    `My experience at ${CLINIC_NAME} for ${treatmentText} has been outstanding from start to finish.`,
    `I had ${treatmentText} done at ${CLINIC_NAME}, Kalyan and I'm extremely happy with the result.`,
    `Choosing ${CLINIC_NAME} for ${treatmentText} turned out to be one of the best decisions for my dental health.`,
  ];

  const doctorLines = [
    `${doctor} is incredibly skilled and took the time to explain every single step before starting.`,
    `${doctor} is gentle, patient and genuinely cares about long-term results — not just a quick fix.`,
    `${doctor} made me feel completely at ease and answered every question I had with patience.`,
    `${doctor}'s precision and warm bedside manner stood out — exactly what you want in a dentist.`,
  ];

  const perksSentence =
    perks.length > 0
      ? `A few things that really stood out: ${joinNicely(perks.map((p) => p.toLowerCase()))}.`
      : "";

  const closers = [
    `Highly recommend ${CLINIC_NAME} to anyone in Kalyan or Dombivli looking for honest, modern dental care.`,
    `If you're searching for a trustworthy dental clinic in Kalyan, ${CLINIC_NAME} is the place to go.`,
    `Five stars without hesitation — ${CLINIC_NAME} sets the bar for dental care in Kalyan.`,
    `Wholeheartedly recommend ${CLINIC_NAME} — you'll be in safe, expert hands.`,
  ];

  const signature = name ? `\n\n— ${name}` : "";

  return [
    pick(openers, seed),
    pick(doctorLines, seed + 1),
    perksSentence,
    pick(closers, seed + 2),
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim() + signature;
}

const WriteReview = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [treatments, setTreatments] = useState<string[]>([]);
  const [doctor, setDoctor] = useState<string>(DOCTORS[0]);
  const [perks, setPerks] = useState<string[]>([]);
  const [review, setReview] = useState("");
  const [history, setHistory] = useState<HistoryItem[]>([]);

  useEffect(() => {
    document.title = "Write a Review — Mutha's Advanced Dental Care";
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch {}
  }, []);

  const toggle = (arr: string[], v: string) =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const canGenerate = useMemo(() => name.trim().length > 1, [name]);

  const clinicalItems = useMemo(() => {
    const set = new Set<string>();
    const seed = treatments.join("|").length + name.length;
    if (treatments.length === 0) {
      DEFAULT_CLINICAL.forEach((i) => set.add(i));
    } else {
      treatments.forEach((t) => {
        (CLINICAL_BY_TREATMENT[t] || DEFAULT_CLINICAL).forEach((i) => set.add(i));
      });
    }
    return shuffle(Array.from(set), seed);
  }, [treatments, name]);

  const handleGenerate = () => {
    if (!canGenerate) {
      toast({ title: "Please add your name", description: "We use it to sign the review." });
      return;
    }
    const text = generateReview({ name: name.trim(), treatments, doctor, perks });
    setReview(text);
    const next: HistoryItem = {
      id: crypto.randomUUID(),
      name: name.trim(),
      review: text,
      createdAt: Date.now(),
    };
    const updated = [next, ...history].slice(0, 10);
    setHistory(updated);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
    } catch {}
  };

  const handleReset = () => {
    setName("");
    setPhone("");
    setTreatments([]);
    setPerks([]);
    setDoctor(DOCTORS[0]);
    setReview("");
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied!", description: "Your review is on the clipboard." });
    } catch {
      toast({ title: "Copy failed", description: "Please select and copy manually." });
    }
  };

  const handlePostToGoogle = async () => {
    if (!review) return;
    await handleCopy(review);
    window.open(GOOGLE_REVIEW_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <header className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium mb-4">
            <Sparkles className="h-3.5 w-3.5" /> Review Generator
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground">
            Share Your Experience
          </h1>
          <p className="mt-2 text-muted-foreground text-sm md:text-base max-w-2xl mx-auto">
            Tell us about your visit and we'll craft a polished Google review for {CLINIC_NAME}. Edit it
            freely, then copy and post in seconds.
          </p>
          <div className="mt-3 flex items-center justify-center gap-1 text-amber-500">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-current" />
            ))}
            <span className="ml-2 text-xs text-muted-foreground">
              4.9 · 500+ reviews · {PHONE_PRIMARY}
            </span>
          </div>
        </header>

        {/* Patient Info */}
        <section className="rounded-2xl border border-border bg-card p-6 mb-6 shadow-sm">
          <h2 className="font-heading text-lg font-semibold mb-1">Patient Information</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Name is required. Phone is optional and stays on this device.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-wide text-muted-foreground">
                Full Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Priya Sharma"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-xs uppercase tracking-wide text-muted-foreground">
                Phone Number
              </Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 9198765 43210"
                inputMode="tel"
                className="mt-1"
              />
            </div>
          </div>
        </section>

        {/* Treatment */}
        <section className="rounded-2xl border border-border bg-card p-6 mb-6 shadow-sm">
          <h2 className="font-heading text-lg font-semibold mb-1">Treatment Details</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Select one or more treatments you received.
          </p>
          <div className="flex flex-wrap gap-2 mb-5">
            {TREATMENTS.map((t) => {
              const active = treatments.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTreatments((arr) => toggle(arr, t))}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border hover:bg-muted"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Treated by Doctor
          </Label>
          <select
            value={doctor}
            onChange={(e) => setDoctor(e.target.value)}
            className="mt-1 w-full sm:w-72 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {DOCTORS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </section>

        {/* Perks */}
        <section className="rounded-2xl border border-border bg-card p-6 mb-6 shadow-sm">
          <h2 className="font-heading text-lg font-semibold mb-1">Clinic Experience</h2>
          <p className="text-xs text-muted-foreground mb-5">
            What did you like about the clinic? Select all that apply.
          </p>
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-primary mb-2">
                <span aria-hidden>🔬</span> Clinical & Technology
                {treatments.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    (tailored to your treatment)
                  </span>
                )}
              </h3>
              <div className="flex flex-wrap gap-2">
                {clinicalItems.map((item) => {
                  const active = perks.includes(item);
                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPerks((arr) => toggle(arr, item))}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-border hover:bg-muted"
                      }`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
            {PERK_GROUPS.map((g) => (
              <div key={g.title}>
                <h3 className="text-sm font-semibold text-primary mb-2">
                  <span aria-hidden>{g.emoji}</span> {g.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {g.items.map((item) => {
                    const active = perks.includes(item);
                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setPerks((arr) => toggle(arr, item))}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:bg-muted"
                        }`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Button onClick={handleGenerate} size="lg" className="flex-1">
            <Sparkles className="h-4 w-4 mr-2" /> Generate Review
          </Button>
          <Button onClick={handleReset} variant="outline" size="lg">
            <RotateCcw className="h-4 w-4 mr-2" /> Reset All
          </Button>
        </div>

        {/* Output */}
        {review && (
          <section className="rounded-2xl border border-primary/30 bg-primary/5 p-6 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-semibold">Your Review</h2>
              <div className="flex items-center gap-1 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
            </div>
            <Textarea
              value={review}
              onChange={(e) => setReview(e.target.value)}
              className="min-h-[180px] bg-background"
            />
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Button onClick={() => handleCopy(review)} variant="outline" className="flex-1">
                <Copy className="h-4 w-4 mr-2" /> Copy Review
              </Button>
              <Button onClick={handlePostToGoogle} className="flex-1">
                <ExternalLink className="h-4 w-4 mr-2" /> Copy & Open Google
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Tip: after Google opens, paste (Ctrl/Cmd + V) into the review box and submit.
            </p>
          </section>
        )}

        {/* History */}
        {history.length > 0 && (
          <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <h2 className="font-heading text-lg font-semibold mb-1">Review History</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Previously generated reviews on this device.
            </p>
            <ul className="space-y-3">
              {history.map((h) => (
                <li key={h.id} className="rounded-lg border border-border bg-background p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{h.name}</span>
                    <button
                      onClick={() => handleCopy(h.review)}
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                    {h.review}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default WriteReview;